/**
 * Pre-signup email gate.
 *
 * Calls the public /auth-precheck-email edge function BEFORE
 * supabase.auth.signInWithOtp / updateUser is invoked. Blocks disposable
 * domains and surfaces "already registered" / "currently banned" states
 * early so the UI can pivot from sign-up → sign-in (or show a ban message)
 * before the OTP send.
 *
 * Fail-CLOSED on network or 5xx errors: a 4xx response with a structured
 * { allowed: false, reason } body is authoritative; any other failure mode
 * blocks the signup with reason `service_unavailable`.
 */

interface PrecheckResult {
  allowed: boolean
  /** Returned when an existing user (banned or not) was found by email_normalized. */
  exists?: boolean
  reason?: string
  /** Server-supplied user-facing message; currently only set for user_banned. */
  message?: string
  /** ISO timestamp for temporary bans; null for permanent bans. */
  bannedUntil?: string | null
}

interface FetchErrorLike {
  statusCode?: number
  data?: PrecheckResult
  response?: { _data?: PrecheckResult }
}

export function useEmailPrecheck() {
  async function precheckEmail(email: string): Promise<PrecheckResult> {
    const config = useRuntimeConfig()
    const url = `${config.public.apiBase}/auth-precheck-email`
    const normalized = email.trim().toLowerCase()

    try {
      const res = await $fetch<PrecheckResult>(url, {
        method: 'POST',
        body: { email: normalized },
      })
      return res
    } catch (err) {
      const e = err as FetchErrorLike
      const data = e?.data ?? e?.response?._data
      if (data && typeof data.allowed === 'boolean') {
        // Server returned structured 4xx response — authoritative.
        return data
      }
      // Network / 5xx / unknown — fail CLOSED.
      console.warn('[useEmailPrecheck] precheck failed, blocking signup:', err)
      return { allowed: false, reason: 'service_unavailable' }
    }
  }

  return { precheckEmail }
}
